import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Location } from '@angular/common';

/** Intervallo di calendario coperto dagli step di una tappa-tour. */
interface TourRange {
  end: string;
  start: string;
}

/** Navigazione verso un tab-servizio: tab di destinazione e, se nota, il codice del servizio. */
export interface ServiceTabNavigation {
  code?: string;
  tab: string;
}

/**ToDo Documentazione */
@Component({
  selector: 'app-itinerary',
  styleUrls: ['./itinerary.component.scss'],
  templateUrl: './itinerary.component.html'
})
export class ItineraryComponent implements OnInit, OnChanges {
  @Input() public days: any;
  /** Con lo split attivo (`enableSplitTransportTabs`) i rimandi dei voli puntano al tab `flights`. */
  @Input() public enableSplitTransportTabs: boolean = false;
  /** Tenant `enableFlightPassengers`: inoltrato a `app-flights` (assente/false ⇒ invariato). */
  @Input() public enableFlightPassengers: boolean = false;
  @Input() public showOptionlServices: any;
  @Input() public replaceShowDescriptionButtonWithNotes: boolean = false;
  public language: string;
  @Input() public urlCitiesImages = '';
  @Input() public enableItineraryViewServices: any;
  @Input() public itineraryMode: string = 'detail';
  @Input() public version: any;
  @Input() public noImageUrl: any;
  @Input() public defaultImages: any = {};
  @Input() public ignoredIsoCodes: any;
  @Input() public enableTravelStepsTab: boolean = false;
  @Input() public hideCity: any;
  @Input() public totalPax: number = 0;
  @Input() public serviceIcons: any;
  @Output() public readonly navigateToTab = new EventEmitter<ServiceTabNavigation>();
  public activeLink: string | undefined;
  public processedDays: any;
  public blocks: any[] = [];
  public cityEndDates: Map<string, string> = new Map();
  public collapsedDays: { [key: string]: boolean } = {};

  private readonly serviceTabMap: { [key: string]: string } = {
    escorted: 'tours',
    flight: 'transfers',
    hotel: 'hotels',
    optional: 'activities',
    transfer: 'transfers',
    vehicle: 'vehicles'
  };

  constructor(public translateService: TranslateService, private location: Location) {
    this.language = translateService.currentLang;
  }

  public get isSynthetic(): boolean {
    return this.itineraryMode === 'synthetic';
  }

  ngOnInit(): void {
    if (!this.enableItineraryViewServices && this.ignoredIsoCodes) {
      const ignoredCodes = this.ignoredIsoCodes.split(',');
      this.days = this.days.filter((day: any) => !ignoredCodes.includes(day.city_code));
    }
    this.processedDays = this.createProcessedDays();
    if (this.isSynthetic) {
      this.blocks = this.buildBlocks(this.days);
    } else {
      // Vista dettaglio: nessun blocco sintetico (stato solo del ramo isSynthetic).
      this.blocks = [];
      if (!this.enableItineraryViewServices) {
        this.cityEndDates = this.buildCityEndDates(this.days);
      }
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.days) {
      this.processedDays = this.createProcessedDays();
    }
    if (changes.days || changes.itineraryMode) {
      this.blocks = this.isSynthetic ? this.buildBlocks(this.days) : [];
    }
  }

  private buildBlocks(days: any[]): any[] {
    if (!days || !Array.isArray(days)) { return []; }

    const tourKeys = days.map((day: any, index: number) =>
      this.getDayTourKey(this.processedDays?.[index] ?? day));

    // L'intervallo di un tour è dato dagli step (dateFrom→dateTo), non dai soli giorni
    // che portano l'escorted: uno step può coprire più giorni di calendario.
    const tourRanges = this.buildTourRanges(tourKeys);

    // Sequenza delle tappe: un soggiorno-città per run (le città *interne* a un tour sono
    // escluse) e una tappa-tour per escorted, ordinate per giorno d'arrivo.
    const stops = [
      ...this.buildCityStops(days, tourRanges),
      ...this.buildTourStops(tourKeys, tourRanges)
    ].sort((a: any, b: any) => this.compareStops(a, b));

    // Ogni tappa termina il giorno d'arrivo della successiva (il giorno di confine è
    // condiviso, presente in entrambe); l'ultima tappa termina all'ultimo giorno
    // dell'itinerario. La regola non dipende dal `city_name` del giorno di confine:
    // il range delle card viene ricostruito sul nuovo intervallo.
    const lastDate = days[days.length - 1]?.date;

    return stops.map((stop: any, index: number) => {
      const endDate = index + 1 < stops.length ? stops[index + 1].arrivalDate : lastDate;
      return stop.type === 'tour'
        ? this.buildTourBlock(tourKeys, stop.key, stop.range, endDate)
        : this.buildCityBlock(days, stop.startIndex, endDate);
    });
  }

  private buildCityStops(days: any[], tourRanges: Map<string, TourRange>): any[] {
    const stops: any[] = [];
    const tourCities = this.collectTourCities();
    let index = 0;

    while (index < days.length) {
      const day = days[index];
      if (!day?.city_name) { index++; continue; }

      let endIndex = index;
      while (endIndex + 1 < days.length && days[endIndex + 1]?.city_name === day.city_name) {
        endIndex++;
      }

      // Una città *interna* a un tour non è una tappa; il giorno di confine condiviso sì.
      if (!this.isRunInsideTourRange(days, index, tourRanges, tourCities)
        && this.isRenderableCityRun(days, index, endIndex)) {
        stops.push({ type: 'city', startIndex: index, arrivalDate: day.date });
      }

      index = endIndex + 1;
    }

    return stops;
  }

  private buildTourStops(tourKeys: any[], ranges: Map<string, TourRange>): any[] {
    const stops: any[] = [];
    const handled = new Set<string>();

    for (const key of tourKeys) {
      if (!key || handled.has(key)) { continue; }
      handled.add(key);
      const range = ranges.get(key);
      stops.push({ type: 'tour', key, range, arrivalDate: range?.start });
    }

    return stops;
  }

  private compareStops(a: any, b: any): number {
    const aArrival = a.arrivalDate || '';
    const bArrival = b.arrivalDate || '';
    if (aArrival !== bArrival) { return aArrival < bArrival ? -1 : 1; }

    // A parità di giorno d'arrivo la città precede il tour.
    return (a.type === 'tour' ? 1 : 0) - (b.type === 'tour' ? 1 : 0);
  }

  private isRenderableCityRun(days: any[], startIndex: number, endIndex: number): boolean {
    for (let j = startIndex; j <= endIndex; j++) {
      if (days[j]?.showDetailsOnItinerary || days[j]?.isIndipendentStay) { return true; }
    }

    return false;
  }

  private isRunInsideTourRange(
    days: any[], startIndex: number,
    ranges: Map<string, TourRange>, tourCities: Set<string>
  ): boolean {
    const start = days[startIndex]?.date;
    if (!start) { return false; }

    let inside = false;
    ranges.forEach((range: TourRange) => {
      // Interno per data d'INIZIO del run (mai la data di fine): un run che *inizia* dentro
      // il tour (range.start compreso, range.end escluso) è una città del tour.
      if (start >= range.start && start < range.end) { inside = true; }
      // Il giorno range.end è condiviso col post-tour: resta una città del tour solo se
      // quel giorno è ancora nel tour (la sua città è tra quelle degli step).
      else if (start === range.end && tourCities.has(days[startIndex]?.city_name)) { inside = true; }
    });

    return inside;
  }

  private collectTourCities(): Set<string> {
    const cities = new Set<string>();
    (this.processedDays || []).forEach((day: any) => {
      (day?.escorteds || []).forEach((escorted: any) => {
        (escorted?.optionals || []).forEach((optional: any) => {
          (optional?.steps || []).forEach((step: any) => {
            if (step?.cityName) { cities.add(step.cityName); }
          });
        });
      });
    });

    return cities;
  }

  private findDayIndex(days: any[], date: string): number {
    if (date) {
      const found = days.findIndex((day: any) => day?.date === date);
      if (found >= 0) { return found; }
    }

    return days.length - 1;
  }

  private buildTourRanges(tourKeys: any[]): Map<string, TourRange> {
    const ranges = new Map<string, TourRange>();

    (this.processedDays || []).forEach((day: any, index: number) => {
      const key = tourKeys[index];
      if (!key) { return; }
      const optional = this.findTourOptional(day, key);
      (optional?.steps || []).forEach((step: any) => {
        if (!step?.dateFrom) { return; }
        const start = step.dateFrom;
        const end = step.dateTo || step.dateFrom;
        const current = ranges.get(key);
        if (!current) {
          ranges.set(key, { end, start });
        } else {
          if (start < current.start) { current.start = start; }
          if (end > current.end) { current.end = end; }
        }
      });
    });

    return ranges;
  }

  private buildCityBlock(days: any[], startIndex: number, endDate: string): any {
    const day = days[startIndex];
    const endIndex = this.findDayIndex(days, endDate);
    const blockDays: any[] = [];
    for (let j = startIndex; j <= endIndex; j++) {
      const processedDay = this.processedDays?.[j] ?? days[j];
      blockDays.push({
        // La card del giorno di confine porta il nome della tappa, non il `city_name` del giorno.
        city_name: day.city_name,
        date: days[j].date,
        index: j,
        number: j + 1,
        rows: this.buildCityDayRows(processedDay, day.city_name)
      });
    }

    return {
      city_name: day.city_name,
      day: this.processedDays?.[startIndex] ?? day,
      days: blockDays,
      endDate: endDate && endDate !== day.date ? endDate : undefined,
      type: 'city'
    };
  }

  private buildTourBlock(tourKeys: any[], key: string, range?: TourRange, blockEnd?: string): any {
    const processed = this.processedDays || [];

    // Gli step del tour (uno per "narrativa-giorno") vengono raccolti da tutti i giorni che lo portano.
    const steps = this.collectTourSteps(processed, key);

    const startDate = range?.start || (steps.length ? steps[0].dateFrom : undefined);
    const rangeEnd = blockEnd || range?.end
      || (steps.length ? (steps[steps.length - 1].dateTo || steps[steps.length - 1].dateFrom) : undefined);

    // "Un tour = un blocco": una card per OGNI giorno di calendario coperto dal tour,
    // anche per i giorni che non portano direttamente l'escorted (step multi-giorno).
    const processedTourDays: any[] = [];
    for (let j = 0; j < processed.length; j++) {
      const day = processed[j];
      if (!day?.date) { continue; }
      if (startDate && rangeEnd && (day.date < startDate || day.date > rangeEnd)) { continue; }
      if (tourKeys[j] && tourKeys[j] !== key) { continue; }
      processedTourDays.push(day);
    }

    const optionals = this.mergeTourOptionals(processedTourDays, key);
    const base = optionals[0] || {};

    const tourDays = processedTourDays.map((day: any, position: number) =>
      this.buildTourDayCard(day, position, steps, processedTourDays));

    const endDate = rangeEnd && rangeEnd !== startDate ? rangeEnd : undefined;

    return {
      type: 'tour',
      day: processedTourDays[0],
      days: tourDays,
      startDate,
      endDate,
      rimandi: this.buildTourRimandi(processedTourDays),
      tour: this.buildTourMeta(base, processedTourDays)
    };
  }

  /** Rimandi ai tab di dettaglio della tappa-tour (solo icona), senza ripetere le righe-servizio. */
  private buildTourRimandi(processedTourDays: any[]): any[] {
    const rimandi: any[] = [];
    const seen = new Set<string>();
    const add = (type: string): void => {
      const tab = this.getServiceTabId(type);
      if (seen.has(tab)) { return; }
      seen.add(tab);
      rimandi.push({ type, label: this.getTabLabel(type) });
    };

    // Il dettaglio del tour vive nel tab "Tours"; seguono i tab dei servizi dei giorni.
    add('escorted');
    processedTourDays.forEach((day: any) => {
      (day?.serviceOrder || []).forEach((entry: any) => add(entry.type));
    });

    return rimandi;
  }

  private getTabLabel(type: string): string {
    switch (this.getServiceTabId(type)) {
      case 'hotels': return 'HOME.SISTEMAZIONI';
      case 'activities': return 'HOME.ACTIVITIES';
      case 'vehicles': return 'HOME.RENT-A-CAR';
      case 'tours': return 'HOME.ESCORTEDS';
      case 'flights': return 'HOME.FLIGHTS';
      case 'transfers': return this.enableSplitTransportTabs ? 'HOME.TRANSFERS' : 'HOME.TRANSPORT';
      default: return 'HOME.TRANSPORT';
    }
  }

  private collectTourSteps(processedDays: any[], key: string): any[] {
    const steps: any[] = [];
    const seen = new Set<string>();

    processedDays.forEach((day: any) => {
      const optional = this.findTourOptional(day, key);
      (optional?.steps || []).forEach((step: any) => {
        const identity = `${step?.dateFrom || ''}|${step?.cityName || ''}`;
        if (seen.has(identity)) { return; }
        seen.add(identity);
        steps.push(step);
      });
    });

    return steps.sort((a: any, b: any) => `${a?.dateFrom || ''}`.localeCompare(`${b?.dateFrom || ''}`));
  }

  private buildTourDayCard(day: any, position: number, steps: any[], processedTourDays: any[]): any {
    // Una card per giorno di calendario: la narrativa è la description dello step che copre
    // il giorno, individuata dal numero nel titolo "GIORNO <n>" (fallback: ordine posizionale).
    const covering = this.findCoveringStep(steps, day?.date);
    const narrativeNumber = position + 1;
    const startIndex = covering
      ? processedTourDays.findIndex((candidate: any) => candidate?.date === covering.dateFrom)
      : -1;
    const offset = startIndex >= 0 ? position - startIndex : 0;
    // Il giorno di confine nel tour porta la città del tour (dallo step), non il `city_name` del giorno.
    const cityName = covering?.cityName || day?.city_name;

    return {
      date: day?.date,
      // Numero assoluto nell'itinerario ("Giorno 3"), non la posizione dentro il tour.
      number: this.absoluteDayNumber(day),
      city_name: cityName,
      texts: this.selectTourDayTexts(covering, narrativeNumber, offset),
      rows: this.buildTourDayRows(day, cityName)
    };
  }

  /**
   * Riga-servizio di una tappa-città: un servizio che porta `cityBefore` appartiene alla città
   * da cui parte, non necessariamente a quella della card. Si scarta solo se non combacia.
   */
  private buildCityDayRows(processedDay: any, cityName: string): any[] {
    return this.buildServiceRows(processedDay).filter((row: any) =>
      !row.cityBefore || this.normalizeCity(row.cityBefore) === this.normalizeCity(cityName));
  }

  /** Nella tappa-tour compaiono solo i servizi che portano `cityBefore` = città della card. */
  private buildTourDayRows(day: any, cityName: string): any[] {
    return this.buildServiceRows(day).filter((row: any) =>
      !!row.cityBefore && this.normalizeCity(row.cityBefore) === this.normalizeCity(cityName));
  }

  private normalizeCity(value: any): string {
    return `${value ?? ''}`.trim().toLowerCase();
  }

  private absoluteDayNumber(day: any): number {
    const index = (this.days || []).findIndex((candidate: any) => candidate?.date === day?.date);

    return index >= 0 ? index + 1 : 0;
  }

  private findCoveringStep(steps: any[], date: string): any {
    let covering: any;
    steps.forEach((step: any) => {
      if (!step?.dateFrom || step.dateFrom > date) { return; }
      if (!covering || step.dateFrom > covering.dateFrom) { covering = step; }
    });

    return covering;
  }

  private selectTourDayTexts(step: any, dayNumber: number, offset: number): string[] {
    const descriptions = step?.descriptions || [];
    if (!descriptions.length) { return []; }

    const withHeading = descriptions.filter((description: any) =>
      this.extractTourDayNumber(description?.text) !== undefined);
    if (withHeading.length > 0) {
      const match = withHeading.find((description: any) =>
        this.extractTourDayNumber(description.text) === dayNumber);

      return match ? this.descriptionTexts(match) : [];
    }

    return descriptions[offset] ? this.descriptionTexts(descriptions[offset]) : [];
  }

  private extractTourDayNumber(text: any): number | undefined {
    if (!text) { return undefined; }
    const match = `${text}`.match(/GIORNO(?:\s|&nbsp;)+(\d+)/i);

    return match ? parseInt(match[1], 10) : undefined;
  }

  private descriptionTexts(description: any): string[] {
    if (!description?.text) { return []; }

    return [this.stripTourHeading(description.text)];
  }

  // Il primo <strong>…</strong> in testa alla narrativa è il titolo del giorno ed è ridondante
  // (la card mostra già data · numero · città). Lo si rimuove con l'eventuale <br/> che segue.
  // Nessun match sulla parola: l'intestazione può essere tradotta ("GIORNO" / "DAY" / "JOUR" / …).
  private stripTourHeading(text: any): string {
    const value = `${text}`;
    const withinParagraph = value.replace(
      /^(\s*<p[^>]*>\s*)<strong[^>]*>[^<]*<\/strong>\s*(?:<br\s*\/?>)?/i, '$1');
    if (withinParagraph !== value) { return withinParagraph; }

    return value.replace(/^\s*<strong[^>]*>[^<]*<\/strong>\s*(?:<br\s*\/?>)?/i, '');
  }

  private buildTourMeta(optional: any, processedDays: any[]): any {
    if (!optional || Object.keys(optional).length === 0) { return undefined; }

    const name = optional.type || optional.name || optional.items?.[0]?.name || '';
    const abstract = optional.description || '';
    const image = optional.image || '';

    const meta: any[] = [];
    const include = this.findDescriptionValue(optional, 'ServiziInclusi');
    const meals = this.collectTourMeals(processedDays);
    const stays = this.collectTourStays(processedDays);
    if (include) { meta.push({ label: 'ITINERARY.TOUR_INCLUDE', value: include }); }
    if (meals) { meta.push({ label: 'ITINERARY.TOUR_MEALS', value: meals }); }
    if (stays) { meta.push({ label: 'ITINERARY.TOUR_STAYS', value: stays }); }

    if (!name && !abstract && !image && meta.length === 0) { return undefined; }
    return { name, abstract, image, meta };
  }

  private findDescriptionValue(optional: any, code: string): string {
    const descriptions = optional?.descriptions;
    if (!Array.isArray(descriptions)) { return ''; }
    const found = descriptions.find((description: any) => description?.code === code);
    return found ? this.stripHtml(found.value) : '';
  }

  private collectTourStays(processedDays: any[]): string {
    const stays: string[] = [];
    processedDays.forEach((day: any) => {
      (day?.hotels || []).forEach((wrapper: any) => {
        (wrapper?.hotels || []).forEach((hotel: any) => {
          if (!hotel?.name) { return; }
          const stay = day.city_name ? `${day.city_name} — ${hotel.name}` : hotel.name;
          if (!stays.includes(stay)) { stays.push(stay); }
        });
      });
    });
    return stays.join(' · ');
  }

  private collectTourMeals(processedDays: any[]): string {
    const meals: string[] = [];
    processedDays.forEach((day: any) => {
      (day?.hotels || []).forEach((wrapper: any) => {
        (wrapper?.hotels || []).forEach((hotel: any) => {
          (hotel?.rooms || []).forEach((room: any) => {
            if (room?.plan_description && !meals.includes(room.plan_description)) {
              meals.push(room.plan_description);
            }
          });
        });
      });
    });
    return meals.join(' · ');
  }

  private stripHtml(value: any): string {
    if (!value) { return ''; }
    return `${value}`.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
  }

  private getDayTourKey(processedDay: any): string | undefined {
    const escorted = processedDay?.escorteds?.[0];
    const optionals = escorted?.optionals || [];
    const withSteps = optionals.find((optional: any) => (optional?.steps || []).length > 0);

    return withSteps ? this.getOptionalTourKey(escorted, withSteps) : undefined;
  }

  private getOptionalTourKey(escorted: any, optional: any): string {
    // Chiave unica per escorted: il solo `type` (o il fallback 'escorted') può fondere
    // due tour distinti. `code` è univoco; in mancanza usiamo l'identità dell'escorted.
    if (optional?.code) { return `code:${optional.code}`; }
    if (escorted?.sequenceNumber) { return `seq:${escorted.sequenceNumber}`; }
    if (escorted?.transaction) { return `txn:${escorted.transaction}`; }

    return `tour:${optional?.type || ''}|${optional?.name || ''}|${optional?.supplier || ''}|${optional?.date || ''}`;
  }

  private findTourOptional(processedDay: any, key: string): any {
    const escorted = processedDay?.escorteds?.[0];
    const optionals = escorted?.optionals || [];

    return optionals.find((optional: any) =>
      (optional?.steps || []).length > 0 && this.getOptionalTourKey(escorted, optional) === key);
  }

  private mergeTourOptionals(blockDays: any[], key: string): any[] {
    let base: any;
    const steps: any[] = [];
    blockDays.forEach((day: any) => {
      const optional = this.findTourOptional(day, key);
      if (!optional) { return; }
      if (!base) { base = optional; }
      (optional.steps || []).forEach((step: any) => steps.push(step));
    });

    if (!base) { return []; }

    const orderedSteps = steps
      .slice()
      .sort((a: any, b: any) => `${a.dateFrom || ''}`.localeCompare(`${b.dateFrom || ''}`));

    return [{ ...base, steps: orderedSteps }];
  }

  private getConsecutiveCityEndDate(days: any[], index: number): string {
    const day = days[index];
    let endIndex = index;
    for (let j = index + 1; j < days.length; j++) {
      if (days[j].city_name === day.city_name) {
        endIndex = j;
      } else {
        break;
      }
    }

    return days[endIndex].date;
  }

  private buildCityEndDates(days: any[]): Map<string, string> {
    const map = new Map<string, string>();
    if (!days || !Array.isArray(days)) { return map; }

    for (let i = 0; i < days.length; i++) {
      const day = days[i];
      if (!day.city_name || !day.showDetailsOnItinerary) { continue; }

      const lastDate = this.getConsecutiveCityEndDate(days, i);

      // Salva solo se la data di fine è diversa da quella di inizio (> 1 notte)
      if (lastDate !== day.date) {
        map.set(day.city_name, lastDate);
      }
    }
    return map;
  }

  private createProcessedDays(): any {
    if (!this.days || !Array.isArray(this.days)) {
      return this.days;
    }

    // Crea una copia profonda dei days per non modificare l'originale
    const processedDays = JSON.parse(JSON.stringify(this.days));

    // Raccoglie tutti gli steps da tutti i giorni
    const allSteps: any[] = [];

    processedDays.forEach((day: any) => {
      if (day.escorteds && day.escorteds.length > 0) {
        day.escorteds.forEach((escorted: any) => {
          if (escorted.optionals && escorted.optionals.length > 0) {
            escorted.optionals.forEach((optional: any) => {
              if (optional.steps && optional.steps.length > 0) {
                optional.steps.forEach((step: any) => {
                  allSteps.push({
                    step: step,
                    escorted: escorted,
                    optional: optional
                  });
                });
              }
            });
          }
        });
      }
    });

    // Pulisce tutti gli steps esistenti nella copia
    processedDays.forEach((day: any) => {
      if (day.escorteds && day.escorteds.length > 0) {
        day.escorteds.forEach((escorted: any) => {
          escorted.optionals = [];
        });
      }
    });

    // Redistribuisce gli steps nei giorni corretti nella copia
    allSteps.forEach(stepData => {
      const stepDate = new Date(stepData.step.dateFrom);
      const targetDay = processedDays.find((day: any) => {
        const dayDate = new Date(day.date);
        return dayDate.getTime() === stepDate.getTime();
      });

      if (targetDay) {
        // Assicura che il giorno target abbia la struttura escorted
        if (!targetDay.escorteds || targetDay.escorteds.length === 0) {
          targetDay.escorteds = [{
            optionals: [],
            notes: stepData.escorted.notes
          }];
        }

        if (!targetDay.escorteds[0].optionals) {
          targetDay.escorteds[0].optionals = [];
        }

        // Trova o crea l'optional corretto per tipo per raggruppare gli steps e preservare i metadati
        let targetOptional = targetDay.escorteds[0].optionals.find((opt: any) => opt.type === stepData.optional.type);

        if (!targetOptional) {
          targetOptional = {
            ...stepData.optional,
            steps: []
          };
          targetDay.escorteds[0].optionals.push(targetOptional);
        }

        targetOptional.steps.push(stepData.step);
      }
    });

    // Sort each day's services chronologically and build serviceOrder
    processedDays.forEach((day: any) => {
      const typeEntries: { type: string; index: number; key: string }[] = [];

      if (Array.isArray(day.flights) && day.flights.length > 0) {
        day.flights.sort((a: any, b: any) =>
          this.getServiceSortKey('flight', a).localeCompare(this.getServiceSortKey('flight', b))
        );
        day.flights.forEach((f: any, idx: number) => {
          typeEntries.push({ type: 'flight', index: idx, key: this.getServiceSortKey('flight', f) });
        });
      }
      if (Array.isArray(day.cars) && day.cars.length > 0) {
        day.cars.sort((a: any, b: any) =>
          this.getServiceSortKey('vehicle', a).localeCompare(this.getServiceSortKey('vehicle', b))
        );
        day.cars.forEach((c: any, idx: number) => {
          typeEntries.push({ type: 'vehicle', index: idx, key: this.getServiceSortKey('vehicle', c) });
        });
      }
      if (Array.isArray(day.transfers) && day.transfers.length > 0) {
        day.transfers.sort((a: any, b: any) =>
          this.getServiceSortKey('transfer', a).localeCompare(this.getServiceSortKey('transfer', b))
        );
        day.transfers.forEach((t: any, idx: number) => {
          typeEntries.push({ type: 'transfer', index: idx, key: this.getServiceSortKey('transfer', t) });
        });
      }
      if (Array.isArray(day.hotels) && day.hotels.length > 0) {
        day.hotels.sort((a: any, b: any) =>
          this.getServiceSortKey('hotel', a).localeCompare(this.getServiceSortKey('hotel', b))
        );
        day.hotels.forEach((h: any, idx: number) => {
          typeEntries.push({ type: 'hotel', index: idx, key: this.getServiceSortKey('hotel', h) });
        });
      }
      if (Array.isArray(day.optionals) && day.optionals.length > 0) {
        day.optionals.sort((a: any, b: any) =>
          this.getServiceSortKey('optional', a).localeCompare(this.getServiceSortKey('optional', b))
        );
        day.optionals.forEach((o: any, idx: number) => {
          typeEntries.push({ type: 'optional', index: idx, key: this.getServiceSortKey('optional', o) });
        });
      }

      typeEntries.sort((a, b) => a.key.localeCompare(b.key));
      day.serviceOrder = typeEntries.map(e => ({ type: e.type, index: e.index }));
    });

    return processedDays;
  }

  private getServiceSortKey(type: string, service: any): string {
    const fallbackDate = '9999-99-99';
    const fallbackHour = '99:99';

    switch (type) {
      case 'flight': {
        const segments = service?.segments;
        const lastSegment = segments?.[segments.length - 1];
        return `${lastSegment?.arrival?.date || fallbackDate}T${lastSegment?.arrival?.hour || fallbackHour}`;
      }
      case 'vehicle':
        return `${service?.pickup?.date || fallbackDate}T${service?.pickup?.hour || fallbackHour}`;
      case 'hotel':
        return `${service?.checkin || fallbackDate}T${service?.serviceHour || fallbackHour}`;
      case 'transfer':
      case 'optional':
        return `${service?.optionals?.[0]?.date || fallbackDate}T${service?.optionals?.[0]?.serviceHour || fallbackHour}`;
      default:
        return `${fallbackDate}T${fallbackHour}`;
    }
  }

  getSingleServiceDay(day: any, type: string, idx: number): any {
    switch (type) {
      case 'flight':
        return { ...day, flights: [day.flights[idx]] };
      case 'vehicle':
        return { ...day, cars: [day.cars[idx]] };
      case 'transfer':
        return {
          ...day,
          transfers: [day.transfers[idx]],
          transfersSuggested: idx === day.transfers.length - 1 ? day.transfersSuggested : []
        };
      case 'hotel':
        return { ...day, hotels: [day.hotels[idx]] };
      case 'optional':
        return { ...day, optionals: [day.optionals[idx]] };
      default:
        return day;
    }
  }

  getAnchorUrl(cityName: string): string {
    const currentUrl = this.location.path();
    return `${currentUrl}#${cityName.toLowerCase()}`;
  }

  isActiveLink(cityName: string): boolean {
    return this.activeLink === cityName.toLowerCase();
  }

  setActiveLink(cityName: string): void {
    this.activeLink = cityName.toLowerCase();
  }

  public getServiceTabId(type: string): string {
    // Con lo split "Voli" (id `flights`) è un tab distinto: il rimando del volo punta lì.
    if (type === 'flight' && this.enableSplitTransportTabs) { return 'flights'; }
    return this.serviceTabMap[type] || 'transfers';
  }

  public goToServiceTab(type: string, code?: string): void {
    this.navigateToTab.emit({ tab: this.getServiceTabId(type), code });
  }

  public toggleDay(blockIndex: number, dayIndex: number): void {
    const key = `${blockIndex}-${dayIndex}`;
    this.collapsedDays[key] = !this.collapsedDays[key];
  }

  public isDayCollapsed(blockIndex: number, dayIndex: number): boolean {
    return !!this.collapsedDays[`${blockIndex}-${dayIndex}`];
  }

  private buildServiceRows(day: any): any[] {
    if (!day || !Array.isArray(day.serviceOrder)) { return []; }
    return day.serviceOrder
      .reduce((rows: any[], entry: any) => rows.concat(this.buildServiceRow(day, entry.type, entry.index)), []);
  }

  private buildServiceRow(day: any, type: string, index: number): any[] {
    switch (type) {
      case 'flight':
        return [this.buildFlightRow(day.flights?.[index])].filter((row: any) => !!row);
      case 'vehicle':
        return [this.buildVehicleRow(day.cars?.[index])].filter((row: any) => !!row);
      case 'transfer':
        return this.buildOptionalServiceRows(day.transfers?.[index], 'transfer');
      case 'hotel':
        return [this.buildHotelRow(day.hotels?.[index])].filter((row: any) => !!row);
      case 'optional':
        return this.buildOptionalServiceRows(day.optionals?.[index], 'optional');
      default:
        return [];
    }
  }

  private buildOptionalServiceRows(wrapper: any, type: string): any[] {
    if (!Array.isArray(wrapper?.optionals)) { return []; }
    return wrapper.optionals
      .map((optional: any) => this.buildOptionalServiceRow(optional, type))
      .filter((row: any) => !!row);
  }

  private buildFlightRow(flight: any): any {
    const segments = flight?.segments || [];
    const first = segments[0];
    const last = segments[segments.length - 1];
    if (!first || !last) { return null; }

    const name = `${first.departure?.airportName || ''} → ${last.arrival?.airportName || ''}`.trim();
    const overnight = last.arrival?.date && first.departure?.date && last.arrival.date !== first.departure.date ? ' (+1)' : '';
    const description = `${first.departure?.hour || ''} → ${last.arrival?.hour || ''}${overnight}`;

    return {
      type: 'flight',
      code: flight?.code,
      name,
      time: first.departure?.hour,
      description
    };
  }

  private buildOptionalServiceRow(optional: any, type: string): any {
    if (!optional) { return null; }

    return {
      type,
      code: optional?.code,
      name: optional.type,
      time: optional.serviceHour,
      description: optional.items?.[0]?.name || '',
      cityBefore: optional?.cityBefore,
      steps: this.buildSteps(optional.steps)
    };
  }

  private buildHotelRow(hotel: any): any {
    const current = hotel?.hotels?.[0];
    if (!current) { return null; }
    const rooms = current.rooms || [];
    const roomParts = rooms
      .reduce((acc: any[], room: any) =>
        acc.concat([room?.room_description, room?.plan_description]), [])
      .filter((value: any) => !!value);
    const description = roomParts.join(' · ');

    return {
      type: 'hotel',
      code: current?.code,
      name: current.name,
      time: hotel.serviceHour,
      description
    };
  }

  private buildVehicleRow(car: any): any {
    const vehicle = car?.vehicles?.[0];
    if (!vehicle) { return null; }

    return {
      type: 'vehicle',
      code: vehicle?.code,
      name: vehicle.model,
      time: car.pickup?.hour
    };
  }

  private buildSteps(steps: any): any[] {
    if (!Array.isArray(steps)) { return []; }
    return steps.map((step: any) => ({
      cityName: step.cityName,
      dateFrom: step.dateFrom,
      dateTo: step.dateTo,
      descriptions: (step.descriptions || [])
        .map((description: any) => description.text)
        .filter((text: any) => !!text)
    }));
  }
}
